// Sprint 5.6 (D10) — Visual checklist for the TodoWrite tool.
// Sprint ai-elements-rendering (Phase 2, T+X) — rebuilt on the AI Elements
// Task primitives (ADR D-026): TaskTrigger header (collapsible, defaultOpen)
// + TaskContent left-rail body. Everything the old card carried is preserved:
// heading, progress counter, cancelled count, in-progress activeForm with
// D-msg-14 truncation, per-row status glyphs, all testids.
//
// Cursor renders TodoWrite as a typed checklist with status icons, NOT
// as a "Run TodoWrite" tool card with a JSON dump. We mirror that.
//
// TodoWrite input shape:
//   { todos: [{ content, status, activeForm }, ...] }
// where status ∈ { pending, in_progress, completed, cancelled }.
// `activeForm` is a present-tense rewriting Anthropic encourages
// ("Reading the file" vs "Read the file") shown as a sub-label.
//
// Subagents (parent_tool_use_id) produce their own TodoWrite calls; those
// are rendered inside the SubagentPanel.

import type { Message } from '../types';
import { asToolUse } from '../lib/messages';
import { useTranslation } from '../i18n';
import { Icon } from './atoms/Icon';
import { Task, TaskTrigger, TaskContent } from './ai-elements/task';

export interface TodoChecklistProps {
  message: Message;
}

interface Todo {
  content: string;
  activeForm?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function parseTodos(input: Record<string, unknown>): Todo[] {
  const raw = input.todos;
  if (!Array.isArray(raw)) return [];
  const out: Todo[] = [];
  for (const t of raw) {
    if (!isObj(t)) continue;
    const content = typeof t.content === 'string' ? t.content : '';
    if (!content) continue;
    const statusRaw = typeof t.status === 'string' ? t.status : 'pending';
    const status: Todo['status'] = (
      ['pending', 'in_progress', 'completed', 'cancelled'].includes(statusRaw)
        ? statusRaw
        : 'pending'
    ) as Todo['status'];
    const todo: Todo = { content, status };
    if (typeof t.activeForm === 'string') todo.activeForm = t.activeForm;
    out.push(todo);
  }
  return out;
}

export function TodoChecklist({ message }: TodoChecklistProps) {
  const { t } = useTranslation();
  const tu = asToolUse(message);
  const todos = parseTodos(tu.input);
  if (todos.length === 0) {
    return (
      <div
        className="text-caption text-muted-foreground/70"
        data-testid="todo-checklist-empty"
      >
        {t('todos.empty')}
      </div>
    );
  }

  const completed = todos.filter((td) => td.status === 'completed').length;
  const cancelled = todos.filter((td) => td.status === 'cancelled').length;
  const inProgress = todos.find((td) => td.status === 'in_progress');

  return (
    <Task
      className="text-body"
      data-testid="todo-checklist"
    >
      <TaskTrigger title={t('todos.heading')}>
        <div className="group flex w-full cursor-pointer items-center gap-2 text-muted-foreground transition-colors hover:text-foreground">
          <Icon name="ListChecks" size={14} className="shrink-0" aria-hidden="true" />
          <span className="shrink-0 font-medium text-foreground">{t('todos.heading')}</span>
          <span className="shrink-0 text-caption text-muted-foreground/70">
            {t('todos.progress', { completed, total: todos.length - cancelled })}
            {cancelled > 0 ? ` · ${t('todos.cancelled', { count: cancelled })}` : ''}
          </span>
          {inProgress && (
            // Sprint messages-section-fixes (D-msg-14) — `min-w-0` is
            // required for `truncate` inside a flex container; without it
            // the ellipsis never kicks in and a long `activeForm`
            // overflows the header on narrow viewports.
            <span
              className="min-w-0 flex-1 text-left text-caption text-status-success truncate"
              title={inProgress.activeForm ?? inProgress.content}
            >
              · {inProgress.activeForm ?? inProgress.content}
            </span>
          )}
          <span className="ml-auto shrink-0 text-muted-foreground/45" aria-hidden="true">
            <Icon
              name="ChevronDown"
              size={12}
              className="transition-transform group-data-[state=open]:rotate-180"
            />
          </span>
        </div>
      </TaskTrigger>
      <TaskContent>
        <ul className="space-y-1.5" data-testid="todo-list">
          {todos.map((todo, idx) => (
            // Sprint messages-section-fixes (D-msg-7) — key is the todo's
            // content, which is stable across reorderings. `idx` would
            // make React re-use child state on the wrong row when Claude
            // marks a todo complete and the list reshuffles. The fallback
            // to `${idx}-${todo.content}` covers the rare duplicate
            // content case.
            <TodoRow
              key={`${idx}-${todo.content}`}
              todo={todo}
            />
          ))}
        </ul>
      </TaskContent>
    </Task>
  );
}

function TodoRow({ todo }: { todo: Todo }) {
  const meta = STATUS_META[todo.status];
  return (
    <li
      className={`flex items-start gap-2 ${meta.lineClass}`}
      data-testid="todo-row"
      data-status={todo.status}
    >
      <span
        className={`mt-0.5 inline-flex w-4 h-4 items-center justify-center text-caption font-bold ${meta.iconClass}`}
        aria-hidden="true"
      >
        {meta.icon}
      </span>
      <span className={`flex-1 ${meta.textClass}`}>
        {todo.content}
        {todo.status === 'in_progress' && todo.activeForm && (
          <span className="block text-caption text-status-success/80 mt-0.5">
            {todo.activeForm}
          </span>
        )}
      </span>
    </li>
  );
}

const STATUS_META: Record<
  Todo['status'],
  {
    icon: string;
    iconClass: string;
    textClass: string;
    lineClass: string;
  }
> = {
  pending: {
    icon: '○',
    iconClass: 'text-muted-foreground/70',
    textClass: 'text-muted-foreground',
    lineClass: '',
  },
  in_progress: {
    icon: '◐',
    iconClass: 'text-status-success',
    textClass: 'text-foreground font-medium',
    lineClass: '',
  },
  completed: {
    icon: '✓',
    iconClass: 'text-status-success',
    textClass: 'text-muted-foreground line-through',
    lineClass: '',
  },
  cancelled: {
    icon: '✕',
    iconClass: 'text-muted-foreground/45',
    textClass: 'text-muted-foreground/45 line-through',
    lineClass: '',
  },
};
